import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid, Environment } from '@react-three/drei';
import { PCCase } from '../../types';
import { CaseModel } from './CaseModel';
import { useThemeStore } from '../../context/themeStore';
import { getSeriesColor } from '../../utils/seriesColors';

interface SceneProps {
  cases: PCCase[];
  showGrid?: boolean;
  overlayMode?: boolean;
  autoRotate?: boolean;
  showDimensions?: boolean;
}

export const Scene: React.FC<SceneProps> = ({
  cases: allCases,
  showGrid = true,
  overlayMode = false,
  autoRotate = false,
  showDimensions = true,
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const cases = allCases.filter((c) => c.dimensions);

  // Calculate scale based on largest case
  const maxDimension = Math.max(
    ...cases.flatMap((c) => [c.dimensions.height, c.dimensions.width, c.dimensions.depth])
  );
  const scale = 2.5 / maxDimension;

  // Ground level (where the grid is)
  const groundY = -1.5;

  // Find max dimensions for alignment
  const maxWidth = Math.max(...cases.map((c) => c.dimensions.width * scale));
  const maxDepth = Math.max(...cases.map((c) => c.dimensions.depth * scale));

  // Calculate positions - align to bottom, right, and back
  const getPosition = (pcCase: PCCase, index: number, total: number): [number, number, number] => {
    const caseHeight = pcCase.dimensions.height * scale;
    const caseWidth = pcCase.dimensions.width * scale;
    const caseDepth = pcCase.dimensions.depth * scale;

    // Y position: place bottom of case on ground
    const y = groundY + caseHeight / 2;

    // Z position: align back of all cases (negative Z is towards camera)
    const z = -(maxDepth - caseDepth) / 2;

    if (overlayMode) {
      // Align all cases to the right edge and back
      const x = (maxWidth - caseWidth) / 2;
      return [x, y, z];
    }

    // Side-by-side: position from right to left with spacing
    const spacing = 0.3;
    let x = 0;
    for (let i = 0; i < index; i++) {
      x -= cases[i].dimensions.width * scale + spacing;
    }
    // Offset to center the group
    const totalGroupWidth = cases.reduce((sum, c) => sum + c.dimensions.width * scale, 0) + (total - 1) * spacing;
    x += totalGroupWidth / 2 - caseWidth / 2;

    return [x, y, z];
  };

  return (
    <div className="w-full h-full min-h-[400px]">
      <Canvas shadows>
        <Suspense fallback={null}>
          <PerspectiveCamera makeDefault position={[4, 3, 5]} fov={50} />

          {/* Lighting */}
          <ambientLight intensity={0.4} />
          <directionalLight
            position={[10, 10, 5]}
            intensity={1}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
          />
          <pointLight position={[-10, -10, -10]} intensity={0.3} />

          {/* Environment for reflections */}
          <Environment preset="studio" />

          {/* Grid */}
          {showGrid && (
            <Grid
              position={[0, -1.5, 0]}
              args={[20, 20]}
              cellSize={0.5}
              cellThickness={0.5}
              cellColor={isDark ? '#2A2A2F' : '#E2E2DD'}
              sectionSize={2}
              sectionThickness={1}
              sectionColor={isDark ? '#3D3D44' : '#C9C9C3'}
              fadeDistance={15}
              fadeStrength={1}
              followCamera={false}
            />
          )}

          {/* Case models */}
          {cases.map((pcCase, index) => (
            <CaseModel
              key={pcCase.id}
              pcCase={pcCase}
              position={getPosition(pcCase, index, cases.length)}
              color={getSeriesColor(index)}
              scale={scale}
              showLabels={true}
              showDimensions={showDimensions}
              autoRotate={autoRotate}
              opacity={overlayMode ? 0.6 : 1}
            />
          ))}

          {/* Controls */}
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI / 1.5}
            minDistance={2}
            maxDistance={15}
          />
        </Suspense>
      </Canvas>
    </div>
  );
};
